import Loader from '@basic/Loader';
import {
  VideoEventData,
  VideoEventName
} from '@components/assignments/task/blocks/slides/hooks/useVideoEventLogger';
import { MediaitemModel } from '@models';
import * as Sentry from '@sentry/nextjs';
import { setupForDark } from '@theme';
import Player, { VimeoUrl } from '@vimeo/player';
import classNames from 'classnames';
import { MediaitemService } from 'lib/services/mediaitems';
import * as _ from 'lodash';
import React, { useEffect, useRef, useState } from 'react';

interface VimeoPlayerProps {
  mediaitem: MediaitemModel;
  className?: string;
  width: number;
  height: number;
  currentTimeSeconds?: number;
  onVideoEvent: (event: VideoEventName, data: VideoEventData) => void;
}

const VimeoPlayer: React.FC<VimeoPlayerProps> = ({
  width,
  height,
  mediaitem,
  className = '',
  currentTimeSeconds,
  onVideoEvent
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const playerInstanceRef = useRef<Player | null>(null);
  const timersRef = useRef<number[]>([]);
  const mediaService = new MediaitemService(mediaitem);
  const [isLoading, setIsLoading] = useState(true);
  const videoSrc = mediaService.getVideoSrc();
  const onVideoEventRef = useRef(onVideoEvent);
  onVideoEventRef.current = onVideoEvent;

  // Function to aggressively constrain Vimeo's wrapper
  const constrainVimeoWrapper = () => {
    if (!containerRef.current) return;

    // NOTE: All this is necessary because the responive vimeo player
    // creates iframe whick ignores the parent settings
    const vimeoWrapper = containerRef.current.querySelector(
      'div[style*="padding"]'
    ) as HTMLDivElement;

    if (vimeoWrapper) {
      //NOTE Aggressively override Vimeo's inline styles with !important
      vimeoWrapper.style.setProperty('max-height', `${height}px`, 'important');
      vimeoWrapper.style.setProperty('height', 'auto', 'important');
      vimeoWrapper.style.setProperty('overflow', 'hidden', 'important');

      // NOTE: For portrait videos, limit the padding-bottom
      const currentPadding = vimeoWrapper.style.paddingBottom;
      if (currentPadding) {
        const paddingValue = parseFloat(currentPadding);
        if (paddingValue > 100 && containerRef.current.offsetWidth > 0) {
          const maxPaddingPercent = (height / containerRef.current.offsetWidth) * 100;
          vimeoWrapper.style.setProperty(
            'padding-bottom',
            `${Math.min(paddingValue, maxPaddingPercent)}%`,
            'important'
          );
        }
      }
    }

    // NOTE: Also constrain the iframe if needed
    const iframe = containerRef.current.querySelector('iframe') as HTMLIFrameElement;
    if (iframe) {
      iframe.style.setProperty('max-height', `${height}px`, 'important');
    }
  };

  useEffect(() => {
    if (!containerRef.current) return;

    // Clean up existing player if it exists
    if (playerInstanceRef.current) {
      playerInstanceRef.current.destroy();
      playerInstanceRef.current = null;
    }

    // Reset loading state when video source changes
    setIsLoading(true);

    try {
      const player = new Player(containerRef.current, {
        url: videoSrc as VimeoUrl,
        responsive: true,
        autopause: false,
        autoplay: false,
        controls: true,
        keyboard: true,
        pip: true,
        playsinline: true,
        preload: 'auto',
        colors: ['2e2e2e', '9D90E1', 'ffffff', '000000'],
        portrait: false,
        vimeo_logo: false
      });

      player.on('loaded', function () {
        setIsLoading(false);

        // Clear any existing timers before scheduling new ones
        timersRef.current.forEach((id) => clearTimeout(id));
        timersRef.current = [];

        constrainVimeoWrapper();
        // NOTE: This is intended as vimeo may adjust the wrapper after loading
        const timer1 = window.setTimeout(constrainVimeoWrapper, 100);
        const timer2 = window.setTimeout(constrainVimeoWrapper, 500);
        timersRef.current.push(timer1, timer2);
      });

      player.on('error', function (error) {
        Sentry.captureMessage(error.message);
        setIsLoading(false);
      });

      player.on('play', function (data) {
        onVideoEventRef.current(VideoEventName.PLAY, {
          seconds: data.seconds,
          duration: data.duration,
          percent: data.percent
        });
      });

      player.on('pause', function (data) {
        if (data.percent <= 0.99) {
          onVideoEventRef.current(VideoEventName.PAUSE, {
            seconds: data.seconds,
            duration: data.duration,
            percent: data.percent
          });
        }
      });

      player.on('seeked', function (data) {
        onVideoEventRef.current(VideoEventName.SEEKED, {
          seconds: data.seconds,
          duration: data.duration,
          percent: data.percent
        });
      });

      player.on('timeupdate', function (data) {
        onVideoEventRef.current(VideoEventName.TIMEUPDATE, {
          seconds: data.seconds,
          duration: data.duration,
          percent: data.percent
        });
      });

      player.on('volumechange', function (data) {
        onVideoEventRef.current(VideoEventName.VOLUME_CHANGE, {
          volume: data.volume,
          muted: data.muted
        });
      });

      player.on('qualitychange', function (data) {
        onVideoEventRef.current(VideoEventName.QUALITY_CHANGE, {
          quality: data.quality
        });
      });

      player.on('fullscreenchange', function (data) {
        onVideoEventRef.current(VideoEventName.FULLSCREEN_CHANGE, {
          fullscreen: data.fullscreen
        });
      });

      player.on('ended', function (data) {
        onVideoEventRef.current(VideoEventName.FINISHED, {
          seconds: data.seconds,
          duration: data.duration,
          percent: data.percent
        });

        player
          .getFullscreen()
          .then((isFullscreen) => isFullscreen && player.exitFullscreen())
          .catch((error) => Sentry.captureException(error));
      });

      playerInstanceRef.current = player;

      // Clean up on unmount
      return () => {
        // Clear all pending timers
        timersRef.current.forEach((id) => clearTimeout(id));
        timersRef.current = [];

        playerInstanceRef.current?.destroy();
        playerInstanceRef.current = null;
      };
    } catch (err) {
      Sentry.captureException(err);
    }
  }, [videoSrc]);

  // Update constraints when height prop changes
  useEffect(() => {
    constrainVimeoWrapper();
  }, [height]);

  // Listen for window resize to reapply constraints
  useEffect(() => {
    const handleResize = () => {
      constrainVimeoWrapper();
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [height]);

  useEffect(() => {
    if (!playerInstanceRef.current || _.isNil(currentTimeSeconds)) return;
    setIsLoading(true);
    playerInstanceRef.current
      .setCurrentTime(currentTimeSeconds)
      .catch((error) => {
        Sentry.captureException(error);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [currentTimeSeconds]);

  return (
    <div
      className='relative mx-auto w-full overflow-hidden'
      style={{
        maxHeight: `${height}px`,
        minHeight: '100px',
        height: 'fit-content'
      }}
    >
      <div ref={containerRef} className='w-full' />
      {isLoading && (
        <div
          className={classNames(
            'absolute inset-0 flex items-center justify-center',
            setupForDark('bg-page bg-opacity-80')
          )}
        >
          <Loader />
        </div>
      )}
    </div>
  );
};

export default VimeoPlayer;
